import React, { useCallback, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";

/**
 * Shared Chart-of-Accounts helpers for the Phase B/C/D finance pages.
 *
 * The ledger auto-posts journals when a liability is accrued, a loan is
 * disbursed, a payable is paid, etc. — so every one of those forms has to tell
 * the backend WHICH GL accounts to debit/credit. Rather than duplicate the
 * coa/list fetch and the account <select> in five pages, both live here.
 */

const authPayload = () => ({
  customer_id: Number(localStorage.getItem("customer_id")),
  api_key: localStorage.getItem("api_key"),
  user_id: Number(localStorage.getItem("user_id")),
  language: "en",
});

/**
 * Loads the full chart of accounts once. Returns { accounts, loading, reload }.
 * Accounts are the raw coa/list rows: { account_id, account_code,
 * account_name, account_type, is_active, ... }.
 */
export function useAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.coaList,
        JSON.stringify({ ...authPayload(), account_type: "", search: "" })
      );
      setAccounts(res?.result ? res.data || [] : []);
    } catch (e) {
      // Non-fatal: the page still renders, the picker is just empty and the
      // form's own validation blocks submit until accounts exist.
      console.error("Failed to load chart of accounts:", e);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { accounts, loading, reload };
}

/**
 * A GL account dropdown. Pass `types` (array of account_type strings) to
 * restrict the options — e.g. types={["asset"]} for a cash/bank picker,
 * types={["expense"]} for an expense account.
 */
export function AccountSelect({
  accounts = [],
  value,
  onChange,
  name,
  types,
  placeholder = "Select account",
  disabled = false,
  isInvalid = false,
}) {
  const options = accounts
    .filter((a) => Number(a.is_active) !== 0)
    .filter((a) => (types && types.length ? types.includes(a.account_type) : true));

  return (
    <Form.Select
      name={name}
      value={value ?? ""}
      onChange={onChange}
      disabled={disabled}
      isInvalid={isInvalid}
    >
      <option value="">{placeholder}</option>
      {options.map((a) => (
        <option key={a.account_id} value={a.account_id}>
          {a.account_code} · {a.account_name}
        </option>
      ))}
    </Form.Select>
  );
}

/**
 * Small banner telling the user to go build accounts first. Shown inside a
 * form when the chart of accounts is empty, because none of these GL-posting
 * actions can work without accounts to post against.
 */
export function NoAccountsWarning() {
  return (
    <div className="fin-inline-warning">
      No GL accounts exist yet, so this action cannot post to the ledger. Create
      them under <strong>Finance → Chart of Accounts</strong> first.
    </div>
  );
}

export { authPayload as financeAuthPayload };
